import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { PlayCircle, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';

export default function StudentModulesPage() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadModules();
  }, []);

  async function loadModules() {
    console.log('🔄 Carregando módulos do aluno...');
    setLoading(true);
    
    try {
      const { data: modulesData } = await api.get('/modules');
      console.log('📚 Módulos recebidos:', modulesData);
      
      if (!modulesData || modulesData.length === 0) {
        console.log('⚠️ Nenhum módulo encontrado');
        setModules([]);
        setLoading(false);
        return;
      }

      // Buscar progresso de cada módulo
      const modulesWithProgress = await Promise.all(
        modulesData.map(async (module) => {
          try {
            // Buscar vídeos do módulo
            const { data: videos } = await api.get(`/videos/module/${module.id}`);
            const totalLessons = videos.filter(v => v.is_published).length;
            
            if (totalLessons === 0) {
              return {
                ...module,
                progress: 0,
                lessonsCompleted: 0,
                totalLessons: 0,
              };
            }

            // Buscar progresso do módulo
            const { data: progressData } = await api.get(`/progress/module/${module.id}`);
            const lessonsCompleted = progressData.filter(p => p.is_completed).length;
            const progress = Math.round((lessonsCompleted / totalLessons) * 100);

            return {
              ...module,
              progress,
              lessonsCompleted,
              totalLessons,
            };
          } catch (error) {
            console.error(`Erro ao carregar progresso do módulo ${module.id}:`, error);
            return {
              ...module,
              progress: 0,
              lessonsCompleted: 0,
              totalLessons: 0,
            };
          }
        })
      );
      
      console.log('✅ Módulos processados:', modulesWithProgress);
      setModules(modulesWithProgress);
      setLoading(false);
    } catch (error) {
      console.error('❌ Erro ao carregar módulos:', error);
      console.error('Detalhes:', error.response?.data);
      console.error('Status:', error.response?.status);
      setModules([]);
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Módulos em progresso (progress > 0 e < 100)
  const continueWatching = modules.filter(m => m.progress > 0 && m.progress < 100).slice(0, 3);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Welcome */}
      <div className="mb-12">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-text-primary mb-3">Bem-vindo de volta! 👋</h1>
        <p className="text-gray-600 dark:text-text-secondary text-lg">Continue de onde parou ou explore novos módulos</p>
      </div>

      {/* Continue Watching */}
      {continueWatching.length > 0 && (
        <section className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-semibold flex items-center gap-3 text-gray-900 dark:text-text-primary">
              <PlayCircle className="text-primary-500" size={28} /> 
              Continuar Assistindo
            </h2>
          </div>
          <div className="flex gap-6 overflow-x-auto pb-6 snap-x snap-mandatory scrollbar-hide">
            {continueWatching.map((module) => (
              <Link
                key={module.id}
                to={`/student/module/${module.id}`}
              >
                <motion.div
                  whileHover={{ scale: 1.03 }}
                  className="bg-gradient-to-br from-background-800 to-background-900 border border-gray-700/50 rounded-3xl overflow-hidden min-w-[340px] snap-start group cursor-pointer shadow-xl hover:shadow-2xl hover:shadow-primary-500/10 transition-all duration-300"
                >
                  <div className="relative">
                    {module.thumbnail_url ? (
                      <img src={module.thumbnail_url} alt={module.title_pt} className="w-full h-48 object-cover" />
                    ) : (
                      <div className="w-full h-48 bg-gradient-to-br from-primary-500/20 via-background-700 to-background-900 flex items-center justify-center relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-transparent"></div>
                        <PlayCircle size={56} className="text-primary-500/40 relative z-10" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <div className="bg-primary-500 rounded-full p-5 shadow-2xl transform group-hover:scale-110 transition-transform duration-300">
                        <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z"/>
                        </svg>
                      </div>
                    </div>
                    
                    {/* Progress Badge */}
                    <div className="absolute top-4 right-4 bg-primary-500/90 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full">
                      {module.progress}% concluído
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="font-bold text-lg text-text-primary mb-2 group-hover:text-primary-500 transition-colors">
                      {module.title_pt}
                    </h3>
                    <p className="text-text-secondary text-sm line-clamp-2 mb-4">
                      {module.description_pt}
                    </p>
                    
                    {/* Progress Bar */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-gray-400">{module.lessonsCompleted} de {module.totalLessons} aulas</span>
                        <span className="text-primary-500 font-semibold">{module.progress}%</span>
                      </div>
                      <div className="h-2 bg-background-700 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${module.progress}%` }}
                          transition={{ duration: 1, ease: "easeOut" }}
                          className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Meus Materiais */}
      <section>
        <h2 className="text-2xl font-semibold mb-8 text-gray-900 dark:text-text-primary">Meus Materiais</h2>
        
        {modules.length === 0 ? (
          <div className="text-center py-20 bg-background-800 border border-gray-700 rounded-3xl">
            <div className="mb-6">
              <svg className="w-24 h-24 mx-auto text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-text-primary mb-2">
              Você ainda não tem acesso a nenhum módulo
            </h3>
            <p className="text-text-secondary">
              Entre em contato com o administrador
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {modules.map((module) => (
              <Link
                key={module.id}
                to={`/student/module/${module.id}`}
              >
                <motion.div
                  whileHover={{ y: -8 }}
                  className="bg-white dark:bg-gradient-to-br dark:from-background-800 dark:to-background-900 border border-gray-200 dark:border-gray-700/50 rounded-3xl overflow-hidden group cursor-pointer shadow-lg hover:shadow-xl dark:hover:shadow-2xl dark:hover:shadow-primary-500/10 transition-all duration-300"
                >
                  <div className="relative">
                    {module.thumbnail_url ? (
                      <img src={module.thumbnail_url} alt={module.title_pt} className="w-full h-52 object-cover" />
                    ) : (
                      <div className="w-full h-52 bg-gradient-to-br from-primary-500/20 via-background-700 to-background-900 flex items-center justify-center relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-transparent"></div>
                        <PlayCircle size={64} className="text-primary-500/40 relative z-10" />
                      </div>
                    )}

                    {/* Status Badge */}
                    {module.progress === 100 && (
                      <div className="absolute top-4 left-4 bg-success/90 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        Concluído
                      </div>
                    )}
                    {module.progress > 0 && module.progress < 100 && (
                      <div className="absolute top-4 left-4 bg-primary-500/90 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        Em progresso
                      </div>
                    )}
                  </div>
                  
                  <div className="p-7">
                    {/* Progress Badge - Movido para cá */}
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-text-primary group-hover:text-primary-500 transition-colors flex-1">
                        {module.title_pt}
                      </h3>
                      <div className={`flex items-center justify-center w-16 h-16 rounded-full border-4 flex-shrink-0 ml-3 ${
                        module.progress === 100 
                          ? 'border-success bg-success/10' 
                          : module.progress > 0 
                          ? 'border-primary-500 bg-primary-500/10' 
                          : 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800'
                      }`}>
                        <span className={`text-lg font-bold ${
                          module.progress === 100 ? 'text-success' : 
                          module.progress > 0 ? 'text-primary-500' : 
                          'text-gray-500 dark:text-gray-400'
                        }`}>
                          {module.progress}%
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 dark:text-text-secondary text-sm line-clamp-2 mb-6">
                      {module.description_pt}
                    </p>
                    
                    {/* Stats Bar */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700/50">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          module.progress === 100 ? 'bg-success' : 
                          module.progress > 0 ? 'bg-primary-500' : 
                          'bg-gray-400 dark:bg-gray-600'
                        }`}></div>
                        <span className="text-gray-600 dark:text-text-secondary text-sm font-medium">
                          {module.lessonsCompleted}/{module.totalLessons} aulas
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-primary-500 font-semibold text-sm group-hover:gap-2.5 transition-all">
                        {module.progress === 100 ? 'Revisar' : module.progress > 0 ? 'Continuar' : 'Começar'}
                        <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
